import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { ROOMS } from "../../config/rooms";
import compressImage from "../../lib/compressImage";
import AddRetreatGuests from "./AddRetreatGuests";
import {
  Users,
  Pencil,
  Plus,
  Check,
  X,
  Loader2,
  Trash2,
  UserPlus,
  UserMinus,
  Archive,
  ArchiveRestore,
  Eye,
  EyeOff,
  AlertTriangle,
  KeyRound,
  Camera,
  Instagram,
  RotateCcw,
} from "lucide-react";

// Format date to DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const GUEST_STEPS = [
  { key: "profile_completed", label: "Profile" },
  { key: "rules_accepted", label: "Rules" },
  { key: "arrival_submitted", label: "Arrival" },
  { key: "legal_submitted", label: "ID" },
  { key: "activities_selected", label: "Activities" },
];

const getOnboardingProgress = (guest) => {
  const onboarding = guest.onboarding || {};
  const done = GUEST_STEPS.filter(s => onboarding[s.key]).length;
  return Math.round((done / GUEST_STEPS.length) * 100);
};

const GuestsTab = ({ guests, headers, onRefresh }) => {
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [addingGuest, setAddingGuest] = useState(false);
  const [addGuestForm, setAddGuestForm] = useState({ name: "", email: "", password: "" });

  const [guestToDelete, setGuestToDelete] = useState(null);
  const [deletingGuestId, setDeletingGuestId] = useState(null);

  const [resetPasswordGuest, setResetPasswordGuest] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const [editingGuest, setEditingGuest] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const [reonboardGuest, setReonboardGuest] = useState(null);
  const [reonboarding, setReonboarding] = useState(false);

  // Archive (soft-delete) state — keeps the guest's data in the DB but blocks login + hides them everywhere.
  const [guestToArchive, setGuestToArchive] = useState(null);
  const [archivingGuestId, setArchivingGuestId] = useState(null);
  const [showAlumni, setShowAlumni] = useState(false);
  const [archivedGuests, setArchivedGuests] = useState([]);
  const [restoringId, setRestoringId] = useState(null);

  const fetchArchived = useCallback(async () => {
    if (!headers) return;
    try {
      const res = await axios.get(`${API}/admin/guests?only_archived=true`, { headers });
      setArchivedGuests(res.data || []);
    } catch (err) {
      console.error("fetchArchived failed", err);
    }
  }, [headers]);

  // Keep the alumni count in sync so the toggle badge stays accurate.
  useEffect(() => { fetchArchived(); }, [fetchArchived, guests]);

  const archiveGuest = async () => {
    if (!guestToArchive) return;
    setArchivingGuestId(guestToArchive.id);
    try {
      const res = await axios.post(`${API}/admin/guests/${guestToArchive.id}/archive`, {}, { headers });
      toast.success(res.data?.message || `${guestToArchive.name} archived`);
      setGuestToArchive(null);
      onRefresh?.();
      fetchArchived();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive guest");
    } finally {
      setArchivingGuestId(null);
    }
  };

  const restoreGuest = async (guest) => {
    setRestoringId(guest.id);
    try {
      const res = await axios.post(`${API}/admin/guests/${guest.id}/restore`, {}, { headers });
      toast.success(res.data?.message || `${guest.name} restored`);
      onRefresh?.();
      fetchArchived();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore guest");
    } finally {
      setRestoringId(null);
    }
  };

  // Toggles whether an archived alumni shows up on the public Alumni Wall.
  // Updates the local list optimistically so the eye icon flips immediately.
  const toggleAlumniWall = async (guest) => {
    const before = archivedGuests;
    const nextValue = !guest.featured_on_alumni_wall;
    setArchivedGuests((prev) =>
      prev.map((g) => (g.id === guest.id ? { ...g, featured_on_alumni_wall: nextValue } : g)),
    );
    try {
      await axios.post(`${API}/admin/guests/${guest.id}/toggle-alumni-wall`, {}, { headers });
      toast.success(
        nextValue
          ? `${guest.name} is now featured on the Alumni Wall`
          : `${guest.name} hidden from the Alumni Wall`,
      );
    } catch (error) {
      // rollback
      setArchivedGuests(before);
      toast.error(error.response?.data?.detail || "Failed to update Alumni Wall");
    }
  };

  const formatArchivedDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch (_) {
      return "";
    }
  };

  const forceReonboard = async () => {
    if (!reonboardGuest) return;
    setReonboarding(true);
    try {
      const res = await axios.post(`${API}/admin/guests/${reonboardGuest.id}/reset-onboarding`, {}, { headers });
      toast.success(res.data?.message || `${reonboardGuest.name} will be sent through onboarding`);
      setReonboardGuest(null);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset onboarding");
    } finally {
      setReonboarding(false);
    }
  };

  const openEditGuest = (guest) => {
    const p = guest.profile || {};
    const a = guest.arrival || {};
    const i = guest.intake || {};
    const d = guest.dietary || {};
    const act = guest.activities || {};
    const ob = guest.onboarding || {};
    setEditForm({
      name: guest.name || "",
      email: guest.email || "",
      preferred_name: p.preferred_name || "",
      date_of_birth: p.date_of_birth || "",
      emergency_contact_name: p.emergency_contact_name || "",
      emergency_contact_phone: p.emergency_contact_phone || "",
      home_address: p.home_address || "",
      social_link: p.social_link || "",
      nationality: p.nationality || "",
      occupation: p.occupation || "",
      current_project: p.current_project || "",
      fun_fact: p.fun_fact || "",
      phone: p.phone || "",
      avatar: p.avatar || "",
      vibe: p.vibe || "",
      coffee_order: p.coffee_order || "",
      hidden_talent: p.hidden_talent || "",
      ask_me_about: p.ask_me_about || "",
      song_on_repeat: p.song_on_repeat || "",
      social_media_ok: p.social_media_ok !== false,
      bringing_pet: !!p.bringing_pet,
      pet_type: p.pet_type || "",
      pet_name: p.pet_name || "",
      pet_breed: p.pet_breed || "",
      pet_photo: p.pet_photo || "",
      // Arrival (including extras)
      arrival_date: a.arrival_date || "",
      arrival_time: a.arrival_time || "",
      departure_date: a.departure_date || "",
      room_name: a.room_name || "",
      transport_method: a.transport_method || "",
      pickup_requested: !!a.pickup_requested,
      flight_number: a.flight_number || "",
      arrival_notes: a.notes || "",
      guests_during_stay: a.guests_during_stay || "",
      accommodation_help: a.accommodation_help || "",
      stay_transport: a.stay_transport || "",
      food_delivery_ack: !!a.food_delivery_ack,
      // Intake (Legal & ID, Rhythm, Lifestyle)
      passport_number: i.passport_number || "",
      visa_info: i.visa_info || "",
      id_photo_front: i.id_photo_front || "",
      id_photo_back: i.id_photo_back || "",
      stay_reason: i.stay_reason || "",
      stay_reason_detail: i.stay_reason_detail || "",
      stay_departure_date: i.stay_departure_date || "",
      why_savi: i.why_savi || "",
      coliving_experience: i.coliving_experience || "",
      daily_routine: i.daily_routine || "",
      remote_work: i.remote_work === true ? true : i.remote_work === false ? false : null,
      working_hours: i.working_hours || "",
      work_needs: i.work_needs || "",
      workshops: i.workshops || "",
      passions: i.passions || "",
      volunteering: i.volunteering === true ? true : i.volunteering === false ? false : null,
      music_preference: i.music_preference || "",
      social_events: i.social_events || "",
      // Dietary
      vegetarian: !!d.vegetarian,
      vegan: !!d.vegan,
      gluten_free: !!d.gluten_free,
      allergies: d.allergies || "",
      dietary_notes: d.other_notes || "",
      // Activity interests
      yoga: !!act.yoga,
      fitness: !!act.fitness,
      hiking: !!act.hiking,
      cooking: !!act.cooking,
      music_interest: !!act.music,
      entrepreneurship: !!act.entrepreneurship,
      creative_work: !!act.creative_work,
      nature: !!act.nature,
      // Onboarding step flags
      profile_completed: !!ob.profile_completed,
      rules_accepted: !!ob.rules_accepted,
      arrival_submitted: !!ob.arrival_submitted,
      legal_submitted: !!ob.legal_submitted,
      rhythm_submitted: !!ob.rhythm_submitted,
      lifestyle_submitted: !!ob.lifestyle_submitted,
      activities_selected: !!ob.activities_selected,
      dietary_submitted: !!ob.dietary_submitted,
      final_confirmed: !!ob.final_confirmed,
    });
    setEditingGuest(guest);
  };

  // Upload an ID photo (front/back) and store the returned URL into editForm
  const uploadIdPhoto = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image");
      return;
    }
    try {
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append("file", compressed);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setEditForm((prev) => ({ ...prev, [key]: res.data.url }));
      toast.success("Uploaded — don't forget to Save Changes");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const saveGuestEdit = async () => {
    if (!editForm.name || !editForm.email) {
      toast.error("Name and email are required");
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/admin/guests/${editingGuest.id}/profile`, editForm, { headers });
      toast.success(`${editForm.name}'s profile updated`);
      setEditingGuest(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingEdit(false);
    }
  };

  const addGuest = async () => {
    if (!addGuestForm.name || !addGuestForm.email || !addGuestForm.password) {
      toast.error("Please fill in name, email and password");
      return;
    }
    if (addGuestForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setAddingGuest(true);
    try {
      await axios.post(`${API}/admin/guests`, addGuestForm, { headers });
      toast.success(`Guest created!\nEmail: ${addGuestForm.email}\nPassword: ${addGuestForm.password}`);
      setAddGuestForm({ name: "", email: "", password: "" });
      setShowAddGuest(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add guest");
    } finally {
      setAddingGuest(false);
    }
  };

  const deleteGuest = async (guestId) => {
    setDeletingGuestId(guestId);
    try {
      await axios.delete(`${API}/admin/guests/${guestId}`, { headers });
      toast.success("Guest removed");
      setGuestToDelete(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove guest");
    } finally {
      setDeletingGuestId(null);
    }
  };

  const resetPassword = async (guestId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await axios.put(`${API}/admin/guests/${guestId}/password`, { new_password: newPassword }, { headers });
      toast.success(`Password updated! New password: ${newPassword}`);
      setResetPasswordGuest(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="font-serif text-lg sm:text-xl text-[#3D4035]">
            {showAlumni ? "Alumni" : "Guests"} ({showAlumni ? archivedGuests.length : guests.length})
          </h2>
          {/* Toggle between live guests and the archived alumni view */}
          {(archivedGuests.length > 0 || showAlumni) && (
            <Button
              size="sm"
              variant={showAlumni ? "default" : "outline"}
              onClick={() => setShowAlumni(!showAlumni)}
              className={`rounded-full text-xs h-7 ${
                showAlumni
                  ? "bg-[#8C9A7C] hover:bg-[#7C8A6C] text-white border-[#8C9A7C]"
                  : "border-stone-200 text-[#3D4035]/70 hover:text-[#3D4035]"
              }`}
              data-testid="toggle-alumni-view-btn"
            >
              <Archive className="w-3.5 h-3.5 mr-1" />
              {showAlumni ? "Back to live guests" : `Alumni (${archivedGuests.length})`}
            </Button>
          )}
        </div>
        <AddRetreatGuests headers={headers} onDone={onRefresh} />
        <Dialog open={showAddGuest} onOpenChange={setShowAddGuest}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#3D4035] hover:bg-[#5D6055] text-white rounded-full" data-testid="add-guest-btn">
              <UserPlus className="w-4 h-4 mr-1.5" />
              Add Guest
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-[#3D4035]">Add a Guest</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-[#3D4035]">Name</Label>
                <Input
                  value={addGuestForm.name}
                  onChange={(e) => setAddGuestForm({ ...addGuestForm, name: e.target.value })}
                  placeholder="First name"
                  className="h-11 rounded-xl"
                  data-testid="add-guest-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3D4035]">Email</Label>
                <Input
                  type="email"
                  value={addGuestForm.email}
                  onChange={(e) => setAddGuestForm({ ...addGuestForm, email: e.target.value })}
                  placeholder="their@email.com"
                  className="h-11 rounded-xl"
                  data-testid="add-guest-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3D4035]">Password</Label>
                <Input
                  value={addGuestForm.password}
                  onChange={(e) => setAddGuestForm({ ...addGuestForm, password: e.target.value })}
                  placeholder="Simple password to share with them"
                  className="h-11 rounded-xl"
                  data-testid="add-guest-password-input"
                />
              </div>
              <p className="text-xs text-[#3D4035]/50">
                Share the email + password with your guest. They&apos;ll complete their own profile, photo and preferences on first login.
              </p>
              <Button
                onClick={addGuest}
                disabled={addingGuest}
                className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full"
                data-testid="submit-add-guest-btn"
              >
                {addingGuest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Guest Account
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Guest cards — switches between live guests and archived alumni */}
      {showAlumni ? (
        archivedGuests.length === 0 ? (
          <div className="bg-white rounded-3xl p-10 border border-stone-100 text-center text-[#3D4035]/50">
            <Archive className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No archived alumni yet.</p>
            <p className="text-xs mt-1 text-[#3D4035]/40">Guests you archive will appear here. Their data stays in the database — you can restore them anytime.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {archivedGuests.map((guest) => (
              <div
                key={guest.id}
                className="bg-stone-50 rounded-2xl p-4 border border-stone-200 opacity-90"
                data-testid={`alumni-card-${guest.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-stone-200 flex items-center justify-center overflow-hidden flex-shrink-0 grayscale">
                    {guest.profile?.avatar ? (
                      <img src={guest.profile.avatar} alt={guest.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[#3D4035]/50 font-serif text-lg">
                        {(guest.name || "?").charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h3 className="font-medium text-[#3D4035] truncate">{guest.name}</h3>
                      <span className="text-[10px] uppercase tracking-wider bg-[#8C9A7C]/20 text-[#5C6A4C] px-1.5 py-0.5 rounded-full font-semibold">Alumni</span>
                      {guest.featured_on_alumni_wall && (
                        <span className="text-[10px] uppercase tracking-wider bg-[#E07A5F]/15 text-[#C85A38] px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-1">
                          <Eye className="w-2.5 h-2.5" /> On Wall
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#3D4035]/60 truncate">{guest.email}</p>
                    {guest.archived_at && (
                      <p className="text-[10px] text-[#3D4035]/45 mt-1">Archived {formatArchivedDate(guest.archived_at)}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 items-end">
                    <button
                      onClick={() => toggleAlumniWall(guest)}
                      className={`p-1.5 rounded-full transition-colors ${
                        guest.featured_on_alumni_wall
                          ? "bg-[#E07A5F]/15 text-[#C85A38] hover:bg-[#E07A5F]/25"
                          : "bg-stone-200 text-[#3D4035]/40 hover:text-[#3D4035]/70 hover:bg-stone-300"
                      }`}
                      title={
                        guest.featured_on_alumni_wall
                          ? "Featured on public Alumni Wall — click to hide"
                          : "Hidden from public Alumni Wall — click to feature (first name + flag only)"
                      }
                      data-testid={`toggle-wall-${guest.id}`}
                    >
                      {guest.featured_on_alumni_wall ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                    <Button
                      size="sm"
                      onClick={() => restoreGuest(guest)}
                      disabled={restoringId === guest.id}
                      className="bg-[#8C9A7C] hover:bg-[#7C8A6C] text-white rounded-full text-xs h-8"
                      data-testid={`restore-guest-${guest.id}`}
                    >
                      {restoringId === guest.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <ArchiveRestore className="w-3.5 h-3.5 mr-1" />
                          Restore
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : guests.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 border border-stone-100 text-center text-[#3D4035]/50">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No guests yet. Add your first guest above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {guests.map((guest) => {
            const progress = getOnboardingProgress(guest);
            const onboarding = guest.onboarding || {};
            const socialOk = guest.profile?.social_media_ok;
            return (
              <div
                key={guest.id}
                className="bg-white rounded-2xl p-4 border border-stone-100"
                data-testid={`guest-card-${guest.id}`}
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-[#F4F1DE] flex items-center justify-center overflow-hidden flex-shrink-0">
                    {guest.profile?.avatar ? (
                      <img src={guest.profile.avatar} alt={guest.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-serif text-lg text-[#E07A5F]">{guest.name?.charAt(0)}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-[#3D4035] truncate">{guest.name}</p>
                      {guest.role === "admin" && (
                        <span className="text-[10px] bg-[#3D4035] text-white px-2 py-0.5 rounded-full">Admin</span>
                      )}
                      {guest.guest_type === "retreat" && (
                        <span className="text-[10px] bg-[#5C6A4C] text-white px-2 py-0.5 rounded-full" data-testid={`retreat-badge-${guest.id}`}>
                          Retreat
                        </span>
                      )}
                      {guest.is_volunteer && (
                        <span
                          className="text-[10px] bg-[#8C9A7C] text-white px-2 py-0.5 rounded-full"
                          data-testid={`volunteer-badge-${guest.id}`}
                        >
                          Team
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#3D4035]/50 truncate">{guest.email}</p>
                    {guest.profile?.occupation && (
                      <p className="text-xs text-[#3D4035]/60 mt-0.5 truncate">{guest.profile.occupation}</p>
                    )}
                  </div>
                  {/* Actions */}
                  <div className="flex gap-1">
                    <button
                      onClick={async () => {
                        try {
                          await axios.put(
                            `${API}/admin/guests/${guest.id}/profile`,
                            { is_volunteer: !guest.is_volunteer },
                            { headers },
                          );
                          toast.success(guest.is_volunteer ? "Unmarked as team member" : "Marked as team member");
                          onRefresh?.();
                        } catch (e) {
                          toast.error("Couldn't update team member status");
                        }
                      }}
                      className={`p-2 rounded-full hover:bg-[#F4F1DE] ${
                        guest.is_volunteer ? "text-[#8C9A7C]" : "text-[#3D4035]/30"
                      }`}
                      title={guest.is_volunteer ? "Unmark as team member" : "Mark as team member"}
                      data-testid={`toggle-volunteer-${guest.id}`}
                    >
                      <Users className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openEditGuest(guest)}
                      className="p-2 rounded-full hover:bg-[#F4F1DE] text-[#3D4035]/50 hover:text-[#3D4035]"
                      title="Edit profile"
                      data-testid={`edit-guest-${guest.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { setResetPasswordGuest(guest); setNewPassword(""); }}
                      className="p-2 rounded-full hover:bg-[#F4F1DE] text-[#3D4035]/50 hover:text-[#3D4035]"
                      title="Reset password"
                      data-testid={`reset-password-${guest.id}`}
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setReonboardGuest(guest)}
                      className="p-2 rounded-full hover:bg-[#F4F1DE] text-[#3D4035]/50 hover:text-[#E07A5F]"
                      title="Force re-onboarding"
                      data-testid={`force-reonboard-${guest.id}`}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setGuestToArchive(guest)}
                      className="p-2 rounded-full hover:bg-[#8C9A7C]/15 text-[#3D4035]/50 hover:text-[#5C6A4C]"
                      title="Archive guest (keeps their data, blocks login)"
                      data-testid={`archive-guest-${guest.id}`}
                    >
                      <UserMinus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setGuestToDelete(guest)}
                      className="p-2 rounded-full hover:bg-red-50 text-[#3D4035]/50 hover:text-red-500"
                      title="Permanently delete guest"
                      data-testid={`remove-guest-${guest.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Onboarding progress */}
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-[#3D4035]/50 uppercase tracking-wide">Onboarding</span>
                    <span className={`text-xs font-medium ${progress === 100 ? "text-[#81B29A]" : "text-[#E07A5F]"}`}>{progress}%</span>
                  </div>
                  <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${progress === 100 ? "bg-[#81B29A]" : "bg-[#E07A5F]"}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {GUEST_STEPS.map(({ key, label }) => (
                      <span
                        key={key}
                        className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${
                          onboarding[key] ? "bg-[#81B29A]/15 text-[#81B29A]" : "bg-stone-100 text-[#3D4035]/40"
                        }`}
                      >
                        {onboarding[key] ? <Check className="w-2.5 h-2.5" /> : null}
                        {label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Extra info once profile is done */}
                <div className="mt-3 pt-3 border-t border-stone-100 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#3D4035]/60">
                  {guest.arrival?.arrival_date && (
                    <span>🛬 {formatDate(guest.arrival.arrival_date)}{guest.arrival.departure_date ? ` → ${formatDate(guest.arrival.departure_date)}` : ""}</span>
                  )}
                  {guest.arrival?.room_name && <span>🛏️ {guest.arrival.room_name}</span>}
                  {guest.profile && (
                    <span className="flex items-center gap-1" title="Social media photo consent">
                      <Instagram className="w-3 h-3" />
                      {socialOk === false ? <span className="text-red-500 font-medium">No photos</span> : <span className="text-[#81B29A]">Photos OK</span>}
                    </span>
                  )}
                  {guest.profile?.bringing_pet && <span>🐾 Bringing a pet</span>}
                  {guest.dietary && (guest.dietary.vegetarian || guest.dietary.vegan || guest.dietary.gluten_free || guest.dietary.allergies) && (
                    <span>
                      🍽️ {[guest.dietary.vegan && "Vegan", guest.dietary.vegetarian && "Vegetarian", guest.dietary.gluten_free && "GF"].filter(Boolean).join(", ")}
                      {guest.dietary.allergies ? ` · Allergies: ${guest.dietary.allergies}` : ""}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit guest dialog */}
      <Dialog open={!!editingGuest} onOpenChange={(open) => !open && setEditingGuest(null)}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#3D4035]">
              Edit {editingGuest?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#3D4035]/60">Name</Label>
                <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-10 rounded-xl" data-testid="edit-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#3D4035]/60">Email (login)</Label>
                <Input value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="h-10 rounded-xl" data-testid="edit-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#3D4035]/60">Nationality</Label>
                <Input value={editForm.nationality || ""} onChange={(e) => setEditForm({ ...editForm, nationality: e.target.value })} className="h-10 rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#3D4035]/60">Phone</Label>
                <Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="h-10 rounded-xl" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs text-[#3D4035]/60">Occupation</Label>
                <Input value={editForm.occupation || ""} onChange={(e) => setEditForm({ ...editForm, occupation: e.target.value })} className="h-10 rounded-xl" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs text-[#3D4035]/60">Current project</Label>
                <Input value={editForm.current_project || ""} onChange={(e) => setEditForm({ ...editForm, current_project: e.target.value })} className="h-10 rounded-xl" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs text-[#3D4035]/60">Fun fact</Label>
                <Input value={editForm.fun_fact || ""} onChange={(e) => setEditForm({ ...editForm, fun_fact: e.target.value })} className="h-10 rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#3D4035]/60">Vibe</Label>
                <select value={editForm.vibe || ""} onChange={(e) => setEditForm({ ...editForm, vibe: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm">
                  <option value="">—</option>
                  <option value="morning">🌅 Morning person</option>
                  <option value="night">🌙 Night owl</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#3D4035]/60">Coffee order</Label>
                <Input value={editForm.coffee_order || ""} onChange={(e) => setEditForm({ ...editForm, coffee_order: e.target.value })} className="h-10 rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#3D4035]/60">Hidden talent</Label>
                <Input value={editForm.hidden_talent || ""} onChange={(e) => setEditForm({ ...editForm, hidden_talent: e.target.value })} className="h-10 rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#3D4035]/60">Ask me about</Label>
                <Input value={editForm.ask_me_about || ""} onChange={(e) => setEditForm({ ...editForm, ask_me_about: e.target.value })} className="h-10 rounded-xl" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs text-[#3D4035]/60">Song on repeat</Label>
                <Input value={editForm.song_on_repeat || ""} onChange={(e) => setEditForm({ ...editForm, song_on_repeat: e.target.value })} className="h-10 rounded-xl" />
              </div>
            </div>

            <div className="border-t border-stone-100 pt-3">
              <p className="text-xs font-medium text-[#3D4035]/60 uppercase tracking-wide mb-3">Onboarding intake details</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Preferred name</Label>
                  <Input value={editForm.preferred_name || ""} onChange={(e) => setEditForm({ ...editForm, preferred_name: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Date of birth</Label>
                  <Input type="date" value={editForm.date_of_birth || ""} onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Emergency contact</Label>
                  <Input value={editForm.emergency_contact_name || ""} onChange={(e) => setEditForm({ ...editForm, emergency_contact_name: e.target.value })} placeholder="Name" className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Emergency phone</Label>
                  <Input value={editForm.emergency_contact_phone || ""} onChange={(e) => setEditForm({ ...editForm, emergency_contact_phone: e.target.value })} placeholder="+34…" className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs text-[#3D4035]/60">Home address</Label>
                  <Input value={editForm.home_address || ""} onChange={(e) => setEditForm({ ...editForm, home_address: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs text-[#3D4035]/60">Social / website link</Label>
                  <Input value={editForm.social_link || ""} onChange={(e) => setEditForm({ ...editForm, social_link: e.target.value })} placeholder="https://…" className="h-10 rounded-xl" />
                </div>
              </div>
            </div>

            <div className="border-t border-stone-100 pt-3">
              <p className="text-xs font-medium text-[#3D4035]/60 uppercase tracking-wide mb-3">Pet 🐾</p>
              <label className="flex items-center gap-2 text-sm text-[#3D4035] cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={!!editForm.bringing_pet}
                  onChange={(e) => setEditForm({ ...editForm, bringing_pet: e.target.checked })}
                  className="rounded"
                  data-testid="edit-bringing-pet-checkbox"
                />
                Bringing a pet
              </label>
              {editForm.bringing_pet && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#3D4035]/60">Type</Label>
                    <select value={editForm.pet_type || ""} onChange={(e) => setEditForm({ ...editForm, pet_type: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm">
                      <option value="">—</option>
                      <option value="dog">🐕 Dog</option>
                      <option value="cat">🐱 Cat</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-[#3D4035]/60">Pet name</Label>
                    <Input value={editForm.pet_name || ""} onChange={(e) => setEditForm({ ...editForm, pet_name: e.target.value })} className="h-10 rounded-xl" />
                  </div>
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-xs text-[#3D4035]/60">Breed</Label>
                    <Input value={editForm.pet_breed || ""} onChange={(e) => setEditForm({ ...editForm, pet_breed: e.target.value })} className="h-10 rounded-xl" />
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-stone-100 pt-3">
              <p className="text-xs font-medium text-[#3D4035]/60 uppercase tracking-wide mb-3">Stay details</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Arrival date</Label>
                  <Input type="date" value={editForm.arrival_date || ""} onChange={(e) => setEditForm({ ...editForm, arrival_date: e.target.value })} className="h-10 rounded-xl" data-testid="edit-arrival-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Arrival time</Label>
                  <Input type="time" value={editForm.arrival_time || ""} onChange={(e) => setEditForm({ ...editForm, arrival_time: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Departure date</Label>
                  <Input type="date" value={editForm.departure_date || ""} onChange={(e) => setEditForm({ ...editForm, departure_date: e.target.value })} className="h-10 rounded-xl" data-testid="edit-departure-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Room</Label>
                  <select value={editForm.room_name || ""} onChange={(e) => setEditForm({ ...editForm, room_name: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm" data-testid="edit-room-select">
                    <option value="">—</option>
                    {Object.values(ROOMS).map((room) => (
                      <option key={room.name} value={room.name}>{room.name}</option>
                    ))}
                    <option value="Not sure yet">Not sure yet</option>
                    {editForm.room_name && editForm.room_name !== "Not sure yet" && !Object.values(ROOMS).some((r) => r.name === editForm.room_name) && (
                      <option value={editForm.room_name}>{editForm.room_name}</option>
                    )}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Transport method</Label>
                  <select value={editForm.transport_method || ""} onChange={(e) => setEditForm({ ...editForm, transport_method: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm" data-testid="edit-transport-method-select">
                    <option value="">—</option>
                    <option value="taxi">🚕 Taxi from Valencia</option>
                    <option value="bus">🚌 Bus + pickup</option>
                    <option value="rental_car">🚗 Rental car</option>
                    <option value="own_car">🚗 Own car</option>
                    <option value="other">Other</option>
                    {editForm.transport_method && !["taxi", "bus", "rental_car", "own_car", "other"].includes(editForm.transport_method) && (
                      <option value={editForm.transport_method}>{editForm.transport_method} (legacy)</option>
                    )}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Flight # (if any)</Label>
                  <Input value={editForm.flight_number || ""} onChange={(e) => setEditForm({ ...editForm, flight_number: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Guests during stay</Label>
                  <select value={editForm.guests_during_stay || ""} onChange={(e) => setEditForm({ ...editForm, guests_during_stay: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm">
                    <option value="">—</option>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                    <option value="maybe">Maybe</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Accommodation help</Label>
                  <select value={editForm.accommodation_help || ""} onChange={(e) => setEditForm({ ...editForm, accommodation_help: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm">
                    <option value="">—</option>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                    <option value="maybe">Maybe</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Transport during stay</Label>
                  <select value={editForm.stay_transport || ""} onChange={(e) => setEditForm({ ...editForm, stay_transport: e.target.value, food_delivery_ack: e.target.value === "none" ? editForm.food_delivery_ack : false })} className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm" data-testid="admin-stay-transport-select">
                    <option value="">—</option>
                    <option value="car">🚗 Car</option>
                    <option value="scooter">🛵 Scooter</option>
                    <option value="none">No vehicle</option>
                  </select>
                </div>
                <label className={`flex items-center gap-2 text-sm col-span-2 sm:col-span-1 self-end h-10 ${editForm.stay_transport === "none" ? "text-[#3D4035]" : "text-[#3D4035]/40"}`}>
                  <input
                    type="checkbox"
                    checked={!!editForm.food_delivery_ack}
                    disabled={editForm.stay_transport !== "none"}
                    onChange={(e) => setEditForm({ ...editForm, food_delivery_ack: e.target.checked })}
                    className="rounded"
                    data-testid="admin-food-delivery-ack"
                  />
                  Food delivery arranged
                </label>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs text-[#3D4035]/60">Arrival notes</Label>
                  <Input value={editForm.arrival_notes || ""} onChange={(e) => setEditForm({ ...editForm, arrival_notes: e.target.value })} placeholder="Late check-in, special requests, etc." className="h-10 rounded-xl" />
                </div>
                <label className="flex items-center gap-2 text-sm text-[#3D4035] col-span-2">
                  <input
                    type="checkbox"
                    checked={!!editForm.pickup_requested}
                    onChange={(e) => setEditForm({ ...editForm, pickup_requested: e.target.checked })}
                    className="rounded"
                  />
                  Pickup requested
                </label>
              </div>
            </div>

            {/* Legal & ID — ADMIN can fill anything in here on behalf of the guest */}
            <div className="border-t border-stone-100 pt-3" data-testid="admin-legal-id-section">
              <p className="text-xs font-medium text-[#3D4035]/60 uppercase tracking-wide mb-3">Legal & ID</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Passport / ID #</Label>
                  <Input value={editForm.passport_number || ""} onChange={(e) => setEditForm({ ...editForm, passport_number: e.target.value })} className="h-10 rounded-xl" data-testid="edit-passport-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Visa (type & expiry)</Label>
                  <Input value={editForm.visa_info || ""} onChange={(e) => setEditForm({ ...editForm, visa_info: e.target.value })} className="h-10 rounded-xl" />
                </div>
                {["id_photo_front", "id_photo_back"].map((key) => (
                  <div key={key} className="space-y-1.5">
                    <Label className="text-xs text-[#3D4035]/60">
                      {key === "id_photo_front" ? "ID photo — front" : "ID photo — back"}
                    </Label>
                    <label className="flex items-center gap-2 cursor-pointer border border-stone-200 rounded-xl p-2 hover:bg-stone-50">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => uploadIdPhoto(e, key)}
                        data-testid={`edit-${key.replace(/_/g, "-")}-input`}
                      />
                      {editForm[key] ? (
                        <img
                          src={`${API.replace(/\/api$/, "")}${editForm[key]}`}
                          alt={key}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center">
                          <Camera className="w-4 h-4 text-[#3D4035]/40" />
                        </div>
                      )}
                      <span className="text-xs text-[#3D4035]/70">
                        {editForm[key] ? "Replace" : "Upload"}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Reason for stay (RD-ley 26/2026) */}
            <div className="border-t border-stone-100 pt-3" data-testid="admin-stay-reason-section">
              <p className="text-xs font-medium text-[#3D4035]/60 uppercase tracking-wide mb-3">Reason for stay (temporary-let cause)</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Reason</Label>
                  <select value={editForm.stay_reason || ""} onChange={(e) => setEditForm({ ...editForm, stay_reason: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm" data-testid="edit-stay-reason-select">
                    <option value="">—</option>
                    <option value="work_relocation">Work — relocation / posting</option>
                    <option value="remote_work">Remote work</option>
                    <option value="studies">Studies / training</option>
                    <option value="medical">Medical treatment</option>
                    <option value="seasonal_work">Seasonal work</option>
                    <option value="family">Family reasons</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Expected departure</Label>
                  <Input type="date" value={editForm.stay_departure_date || ""} onChange={(e) => setEditForm({ ...editForm, stay_departure_date: e.target.value })} className="h-10 rounded-xl" data-testid="edit-stay-departure-input" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs text-[#3D4035]/60">Detail (goes on the contract)</Label>
                  <Input value={editForm.stay_reason_detail || ""} onChange={(e) => setEditForm({ ...editForm, stay_reason_detail: e.target.value })} className="h-10 rounded-xl" data-testid="edit-stay-detail-input" />
                </div>
              </div>
            </div>
            <div className="border-t border-stone-100 pt-3" data-testid="admin-rhythm-section">
              <p className="text-xs font-medium text-[#3D4035]/60 uppercase tracking-wide mb-3">Savi Rhythm</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs text-[#3D4035]/60">Why Savi</Label>
                  <Input value={editForm.why_savi || ""} onChange={(e) => setEditForm({ ...editForm, why_savi: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Coliving experience</Label>
                  <select value={editForm.coliving_experience || ""} onChange={(e) => setEditForm({ ...editForm, coliving_experience: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm">
                    <option value="">—</option>
                    <option value="first_time">First time</option>
                    <option value="focused">Focused</option>
                    <option value="social">Social</option>
                    <option value="professional">Professional</option>
                    <option value="creative">Creative</option>
                    <option value="wellness">Wellness</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Works remotely</Label>
                  <select
                    value={editForm.remote_work === true ? "yes" : editForm.remote_work === false ? "no" : ""}
                    onChange={(e) => setEditForm({ ...editForm, remote_work: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}
                    className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm"
                  >
                    <option value="">—</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs text-[#3D4035]/60">Daily routine</Label>
                  <Input value={editForm.daily_routine || ""} onChange={(e) => setEditForm({ ...editForm, daily_routine: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Working hours</Label>
                  <Input value={editForm.working_hours || ""} onChange={(e) => setEditForm({ ...editForm, working_hours: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Work needs</Label>
                  <Input value={editForm.work_needs || ""} onChange={(e) => setEditForm({ ...editForm, work_needs: e.target.value })} className="h-10 rounded-xl" />
                </div>
              </div>
            </div>

            {/* Lifestyle */}
            <div className="border-t border-stone-100 pt-3" data-testid="admin-lifestyle-section">
              <p className="text-xs font-medium text-[#3D4035]/60 uppercase tracking-wide mb-3">Lifestyle</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs text-[#3D4035]/60">Workshops of interest</Label>
                  <Input value={editForm.workshops || ""} onChange={(e) => setEditForm({ ...editForm, workshops: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs text-[#3D4035]/60">Passions</Label>
                  <Input value={editForm.passions || ""} onChange={(e) => setEditForm({ ...editForm, passions: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Open to being a team member</Label>
                  <select
                    value={editForm.volunteering === true ? "yes" : editForm.volunteering === false ? "no" : ""}
                    onChange={(e) => setEditForm({ ...editForm, volunteering: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}
                    className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm"
                  >
                    <option value="">—</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#3D4035]/60">Music preference</Label>
                  <select value={editForm.music_preference || ""} onChange={(e) => setEditForm({ ...editForm, music_preference: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm">
                    <option value="">—</option>
                    <option value="tunes">Loves tunes</option>
                    <option value="quiet">Prefers quiet</option>
                  </select>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs text-[#3D4035]/60">Social events</Label>
                  <select value={editForm.social_events || ""} onChange={(e) => setEditForm({ ...editForm, social_events: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm">
                    <option value="">—</option>
                    <option value="yes">Yes — count me in</option>
                    <option value="sometimes">Sometimes</option>
                    <option value="no">No, prefer quiet</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Dietary */}
            <div className="border-t border-stone-100 pt-3" data-testid="admin-dietary-section">
              <p className="text-xs font-medium text-[#3D4035]/60 uppercase tracking-wide mb-3">Dietary</p>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { k: "vegetarian", label: "Vegetarian" },
                  { k: "vegan", label: "Vegan" },
                  { k: "gluten_free", label: "Gluten free" },
                ].map((d) => (
                  <label key={d.k} className="flex items-center gap-2 text-sm text-[#3D4035] cursor-pointer">
                    <input type="checkbox" checked={!!editForm[d.k]} onChange={(e) => setEditForm({ ...editForm, [d.k]: e.target.checked })} className="rounded" />
                    {d.label}
                  </label>
                ))}
              </div>
              <div className="space-y-1.5 mb-3">
                <Label className="text-xs text-[#3D4035]/60">Allergies</Label>
                <Input value={editForm.allergies || ""} onChange={(e) => setEditForm({ ...editForm, allergies: e.target.value })} className="h-10 rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#3D4035]/60">Other dietary notes</Label>
                <Input value={editForm.dietary_notes || ""} onChange={(e) => setEditForm({ ...editForm, dietary_notes: e.target.value })} className="h-10 rounded-xl" />
              </div>
            </div>

            {/* Activity interests */}
            <div className="border-t border-stone-100 pt-3" data-testid="admin-activities-section">
              <p className="text-xs font-medium text-[#3D4035]/60 uppercase tracking-wide mb-3">Activity interests</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { k: "yoga", label: "🧘 Yoga" },
                  { k: "fitness", label: "💪 Fitness" },
                  { k: "hiking", label: "🥾 Hiking" },
                  { k: "cooking", label: "🍳 Cooking" },
                  { k: "music_interest", label: "🎵 Music" },
                  { k: "entrepreneurship", label: "🚀 Entrepreneurship" },
                  { k: "creative_work", label: "🎨 Creative work" },
                  { k: "nature", label: "🌿 Nature" },
                ].map((a) => (
                  <label key={a.k} className="flex items-center gap-2 text-sm text-[#3D4035] cursor-pointer">
                    <input type="checkbox" checked={!!editForm[a.k]} onChange={(e) => setEditForm({ ...editForm, [a.k]: e.target.checked })} className="rounded" />
                    {a.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Onboarding step toggles */}
            <div className="border-t border-stone-100 pt-3" data-testid="admin-onboarding-flags-section">
              <p className="text-xs font-medium text-[#3D4035]/60 uppercase tracking-wide mb-3">Onboarding steps</p>
              <p className="text-xs text-[#3D4035]/50 mb-3">Toggle a step on once you&apos;ve filled in their info on their behalf so their progress bar updates.</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { k: "profile_completed", label: "Profile" },
                  { k: "rules_accepted", label: "Rules" },
                  { k: "arrival_submitted", label: "Arrival" },
                  { k: "legal_submitted", label: "Legal & ID" },
                  { k: "rhythm_submitted", label: "Rhythm" },
                  { k: "lifestyle_submitted", label: "Lifestyle" },
                  { k: "activities_selected", label: "Activities" },
                  { k: "dietary_submitted", label: "Dietary" },
                  { k: "final_confirmed", label: "Final confirm" },
                ].map((s) => (
                  <label key={s.k} className="flex items-center gap-2 text-sm text-[#3D4035] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editForm[s.k]}
                      onChange={(e) => setEditForm({ ...editForm, [s.k]: e.target.checked })}
                      className="rounded"
                      data-testid={`onboarding-flag-${s.k}`}
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-[#3D4035] cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.social_media_ok !== false}
                onChange={(e) => setEditForm({ ...editForm, social_media_ok: e.target.checked })}
                className="rounded"
              />
              📸 OK to appear in social media photos
            </label>

            {/* Intake answers are now ALL editable in the sections above — no separate read-only block */}

            <Button
              onClick={saveGuestEdit}
              disabled={savingEdit}
              className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full"
              data-testid="save-guest-edit-btn"
            >
              {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetPasswordGuest} onOpenChange={(open) => !open && setResetPasswordGuest(null)}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#3D4035]">
              Reset password — {resetPasswordGuest?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 6 characters)"
              className="h-11 rounded-xl"
              data-testid="new-password-input"
            />
            <Button
              onClick={() => resetPassword(resetPasswordGuest.id)}
              className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full"
              data-testid="submit-new-password-btn"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Set New Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Force re-onboarding confirmation dialog */}
      <Dialog open={!!reonboardGuest} onOpenChange={(open) => !open && !reonboarding && setReonboardGuest(null)}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#3D4035] flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-[#E07A5F]" />
              Force re-onboarding?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#3D4035]/70">
            <span className="font-medium">{reonboardGuest?.name}</span> will be sent back through the onboarding wizard on their next login or page refresh.
          </p>
          <p className="text-xs text-[#3D4035]/55">
            Their existing profile, intake answers, ID and stay details stay intact — only the &quot;step complete&quot; flags reset, so they re-confirm each step.
          </p>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => setReonboardGuest(null)} disabled={reonboarding}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              onClick={forceReonboard}
              disabled={reonboarding}
              className="flex-1 bg-[#E07A5F] hover:bg-[#D26A4F] text-white rounded-full"
              data-testid="confirm-force-reonboard-btn"
            >
              {reonboarding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Reset onboarding
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive (soft-remove) confirmation dialog */}
      <Dialog open={!!guestToArchive} onOpenChange={(open) => !open && !archivingGuestId && setGuestToArchive(null)}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#3D4035] flex items-center gap-2">
              <Archive className="w-5 h-5 text-[#5C6A4C]" />
              Archive {guestToArchive?.name}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-[#3D4035]/70">
            <p>This blocks them from logging in and hides them from the People tab, AI matches, leaderboard and daily digest.</p>
            <p className="text-xs text-[#3D4035]/55">
              Their profile, RSVPs, badges and GDPR record stay in the database — you can restore them anytime from the Alumni view.
            </p>
          </div>
          <div className="flex gap-2 pt-3">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => setGuestToArchive(null)}
              disabled={!!archivingGuestId}
            >
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              onClick={archiveGuest}
              disabled={archivingGuestId === guestToArchive?.id}
              className="flex-1 bg-[#8C9A7C] hover:bg-[#7C8A6C] text-white rounded-full"
              data-testid="confirm-archive-guest-btn"
            >
              {archivingGuestId === guestToArchive?.id ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Archive className="w-4 h-4 mr-1" />
              )}
              Archive
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete (hard purge) confirmation dialog */}
      <Dialog open={!!guestToDelete} onOpenChange={(open) => !open && setGuestToDelete(null)}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#3D4035] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Permanently delete {guestToDelete?.name}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#3D4035]/60">
            This permanently removes their account and all their data from the database. <strong>This can&apos;t be undone</strong> — if you just want to remove them from the live app but keep their data, use <em>Archive</em> instead.
          </p>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => setGuestToDelete(null)}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              onClick={() => deleteGuest(guestToDelete.id)}
              disabled={deletingGuestId === guestToDelete?.id}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full"
              data-testid="confirm-delete-guest-btn"
            >
              {deletingGuestId === guestToDelete?.id ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1" />
              )}
              Delete forever
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GuestsTab;
